export const AdministratorRole = 1;
export const StandardRole = 2;
export const Triaxle = 1;
export const TriaxleSoilQuantity = "Triaxle (10 m3)";
export const Trailer = 2;
export const TrailerSoilQuantity = "Trailer (40 m3)";
export const USMQRCodeSize = 130;
export const DefaultMaterialTypeId = 1;
export const DefaultTruckTypeId = 1;

export const Roles = [
  { value: AdministratorRole, label: "Administrator" },
  { value: StandardRole, label: "Standard" },
];

export const ScaleViewType = {
  WeighIn: "WeighIn",
  Tare: "Tare",
};

export const TruckTypes = [
  { value: Triaxle, label: "Triaxle" },
  { value: Trailer, label: "Trailer" },
];

export const enum ScaleWeighTypes {
  Weight = 1,
  Tare = 2,
}

export const PhoneRegExp =
  /^(\+\d{1,2}\s?)?1?\-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;

export const getRoleName = (roleId: number) => {
  const role = Roles.find((r) => r.value === roleId);
  return role ? role.label : "";
};

export const isAdmin = (roleId: number | string | undefined) => {
  if (roleId) {
    return Number(roleId) === AdministratorRole;
  }
  return false;
};

export const a11yProps = (index: number) => {
  return {
    id: `scrollable-auto-tab-${index}`,
    "aria-controls": `scrollable-auto-tabpanel-${index}`,
  };
};

export const HydrovacDistanceOptions = ["Less than 50m", "More than 50m", "Unknown"];
export const HydrovacYesNoOptions = ["Yes", "No", "Unknown"];

export const HydrovacScreeningQuestions = [
  { field: "q1_ContaminatedSite", label: "1. Is the property known to be a contaminated site?", type: "yesno" as const },
  { field: "q2_CommercialIndustrial", label: "2. Is the property used for commercial or industrial purposes?", type: "yesno" as const },
  { field: "q3_NeighbouringCommercial", label: "3. Are the neighbouring properties used for commercial or industrial purposes?", type: "yesno" as const },
  { field: "q4_DistanceToGasStation", label: "4. What is the distance to the nearest gas station/fuel storage tank?", type: "distance" as const },
  { field: "q5_DistanceToDryCleaner", label: "5. What is the distance to the nearest dry cleaner?", type: "distance" as const },
  { field: "q6_SoilStainedOdourous", label: "6. Is the soil stained or odourous?", type: "yesno" as const },
  { field: "q7_OtherMaterialPresent", label: "7. Is any material other than soil and supply water present in the load (e.g plastic, roots)?", type: "yesno" as const },
  { field: "q8_FromRecyclingFacility", label: "8. Did the soil come from a soil recycling facility?", type: "yesno" as const },
  { field: "q9_WaterTaken", label: "9. Water Taken", type: "yesno" as const },
];

// Default screening answers seeded on the hydrovac receive dialog. Each answer
// can be changed manually before receiving; existing (driver-supplied) answers
// are never overwritten.
export const DefaultHydrovacScreeningAnswers = {
  q1_ContaminatedSite: "No",
  q2_CommercialIndustrial: "No",
  q3_NeighbouringCommercial: "No",
  q4_DistanceToGasStation: "More than 50m",
  q5_DistanceToDryCleaner: "More than 50m",
  q6_SoilStainedOdourous: "No",
  q7_OtherMaterialPresent: "No",
  q8_FromRecyclingFacility: "No",
  q9_WaterTaken: "No",
};

export const HydrovacInspectionQuestions = [
  { field: "inspect1_OrganicMaterial", label: "1. Does the fill contain plants, grass, wood, garden waste, or other organic material?" },
  { field: "inspect2_Insects", label: "2. Does the fill contain any insects (e.g especially Asian longhorn beetle)?" },
  { field: "inspect3_UnusualOdours", label: "3. Are there any unusual odours or discolouration of the fill?" },
  { field: "inspect4_OtherMaterial", label: "4. Is any material other than water and soil present in the load (if so, describe below)?" },
  { field: "inspect5_HydrocarbonSheen", label: "5. Is there any evidence of a hydrocarbon film or sheen?" },
  { field: "inspect6_HeadspaceAbove200", label: "6. Is the headspace reading above 200 ppm (combustible gas as hexane)?" },
];

export const ScreeningFlagRules = [
  { field: "q1_ContaminatedSite", trigger: ["Yes"], label: "Known contaminated site" },
  { field: "q4_DistanceToGasStation", trigger: ["Less than 50m", "Unknown"], label: "Near gas station/fuel storage" },
  { field: "q5_DistanceToDryCleaner", trigger: ["Less than 50m", "Unknown"], label: "Near dry cleaner" },
  { field: "q6_SoilStainedOdourous", trigger: ["Yes"], label: "Soil stained or odourous" },
  { field: "q7_OtherMaterialPresent", trigger: ["Yes"], label: "Other material present" },
  { field: "q8_FromRecyclingFacility", trigger: ["Yes"], label: "From recycling facility" },
];

export const getScreeningFlags = (ticket: any): string[] =>
  ScreeningFlagRules.filter((r) => r.trigger.includes(ticket[r.field])).map((r) => r.label);

export const isScreeningFlagged = (ticket: any): boolean =>
  ScreeningFlagRules.some((r) => r.trigger.includes(ticket[r.field]));

// Fallback for Settings.ticketGenerateMaxRange / ticketExportMaxRange, used before
// settings load. Mirrors Constants.DefaultTicketGenerateMaxRange on the server.
export const MaxTicketRange = 50000;

// Bulk Update and Bulk Clear keep the old cap: unlike generate and export, those
// endpoints still load whole tables and have not been reworked for large ranges.
export const BulkOperationMaxRange = 2000;

export type StreamKey = "main" | "small-quantity" | "hydrovac" | "rubble";

// API route prefix per stream. These were duplicated across the four service files;
// centralised here so shared components (e.g. the Ranges tab) can be stream-generic.
export const StreamApiBase: Record<StreamKey, string> = {
  main: "/api/ticket",
  "small-quantity": "/api/ticket/small-quantity",
  hydrovac: "/api/ticket/hydrovac",
  rubble: "/api/ticket/rubble",
};

// Stream choices for report dropdowns (royalty, digital tickets). Shared so every
// report names the streams identically; the values are sent verbatim to the API.
export const StreamOptions: { value: StreamKey; label: string }[] = [
  { value: "main", label: "Main Pit" },
  { value: "small-quantity", label: "Small Quantities" },
  { value: "hydrovac", label: "Hydrovac" },
  { value: "rubble", label: "Rubble" },
];

// The royalty report can also run every stream at once, grouped by stream. Kept apart
// from StreamOptions so the other report dropdowns do not offer "All Streams".
export type RoyaltyStreamKey = StreamKey | "all";

export const RoyaltyStreamOptions: { value: RoyaltyStreamKey; label: string }[] = [
  ...StreamOptions,
  { value: "all", label: "All Streams" },
];

export const royaltyStreamLabel = (stream: string) =>
  RoyaltyStreamOptions.find((s) => s.value === stream)?.label ?? stream;

// Streams whose royalty is calculated on the Settings net ticket price instead of the
// sale price. Mirrors RoyaltyStreams.UsesNetPrice on the server.
export const NetPriceStreams: StreamKey[] = ["small-quantity", "hydrovac"];

export const PaymentMethods = ["Cash", "Cheque", "EFT", "Account"];

// Payment method seeded on the receive dialog (Small Quantity + Rubble). Can be
// changed manually before receiving.
export const DefaultPaymentMethod = "Cheque";

// Default sale prices seeded on the receive dialog per stream. A selected
// project's salePrice overrides these, and they can be edited manually.
export const DefaultSalePrices = {
  Hydrovac: 290,
  SmallQuantity: 150,
  Rubble: 80,
};

// Grouped by office: group 1 = Main + Rubble, group 2 = Small Quantities + Hydrovac.
export const Streams: { key: StreamKey; label: string; bg: string; color: string; group: number }[] = [
  { key: "main", label: "Main Tickets", bg: "#032e5b", color: "#fff", group: 1 },
  { key: "rubble", label: "Rubble", bg: "#FFE0B2", color: "#e65100", group: 1 },
  { key: "small-quantity", label: "Small Quantities", bg: "#90caf9", color: "#0d47a1", group: 2 },
  { key: "hydrovac", label: "Hydrovac", bg: "#e0e0e0", color: "#424242", group: 2 },
];

// The status colours already in use across the portal — surplus green, caution amber,
// deficit red, informational blue — named once so the reports stop repeating hex codes
// and a change of brand is a change in one place. Brand navy stays on the theme palette.
export const StatusColors = {
  positive: "#2e7d32",
  positiveBg: "#e8f5e9",
  warning: "#b26a00",
  warningBg: "#fff8e1",
  negative: "#c62828",
  negativeBg: "#ffebee",
  info: "#0d47a1",
  infoBg: "#e3f2fd",
} as const;

// Why the royalty report skipped an unused ticket — shown beside each one and in its export.
export const SkippedTicketReason = "Marked received, never written up for a customer";
